import React, { FC, memo, useEffect, useState, useRef, useCallback } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { AppRoutes } from 'app/config/routerConfig/types';
import { useScreenBreakpoints } from 'shared/utils/hooks/useScreenBreakpoints';
import { HistoryAction, useGetPaginatedHistoryQuery } from 'entities/History';
import { HistoryActionRow } from 'widgets/HistoryActionRow';
import { Divider } from 'antd';
import { getLoggedUserData, useGetUserByIdQuery } from 'entities/User';
import { useAppSelector } from 'app/config/storeConfig/hooks';
import { Button } from 'shared/ui/Button';

export const MobileMyLatestActivityPage: FC = memo(() => {
  const { t } = useAppTranslation(['profile', 'booking', 'common']);
  const { userId } = useParams();
  const { isDesktop } = useScreenBreakpoints();
  const navigate = useNavigate();

  const { data: user } = useGetUserByIdQuery({ userId, params: { warehouses: true, userDebt: true } });
  const loggedUser = useAppSelector(getLoggedUserData);

  const limit = 10;

  const [page, setPage] = useState(1);
  const [allHistoryActions, setAllHistoryActions] = useState<HistoryAction[]>([]);
  const [isLastPage, setIsLastPage] = useState(false);

  const { data, isFetching } = useGetPaginatedHistoryQuery({ userName: user?.email, limit, page }, { skip: !user?.email });

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isDesktop && userId) {
      navigate(`${AppRoutes.PROFILE}/${userId}`);
    }
  }, [isDesktop, navigate, userId]);

  useEffect(() => {
    if (data?.content) {
      setAllHistoryActions((prev) => {
        const newActions = data.content.filter((newAction) => !prev.some((action) => action.actionId === newAction.actionId));
        return [...prev, ...newActions];
      });
      setIsLastPage(Boolean(data.isLastPage));
    }
  }, [data]);

  const handleScroll = useCallback(() => {
    const el = containerRef.current;
    if (!el || isFetching || isLastPage) {
      return;
    }

    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 50;

    if (nearBottom) {
      setPage((prev) => prev + 1);
    }
  }, [isFetching, isLastPage]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return;
    }

    el.addEventListener('scroll', handleScroll);

    return () => {
      el.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  if (!userId) {
    return <div>{t('User not exists')}</div>;
  }

  return (
    <div className="px-4">
      <div className="flex flex-col p-4 bg-white rounded-lg gap-2">
        <div ref={containerRef} className="space-y-2.5 max-h-[calc(100vh-theme(spacing.headerHeight)-140px)] pr-2 overflow-y-auto">
          {allHistoryActions.length ? (
            allHistoryActions.map((action, index, array) => (
              <div key={action.actionId}>
                <HistoryActionRow data={action} loggedUserEmail={loggedUser?.email} />
                {index < array.length - 1 ? <Divider className="border-secondaryAccent ml-[20px]" type="vertical" /> : null}
              </div>
            ))
          ) : (
            <div className="text-xl text-primaryLight">{t('No data', { ns: 'common' })}</div>
          )}

          {isFetching && <div className="text-center py-2">{t('Loading...')}</div>}
        </div>
      </div>
      <div className="fixed bottom-0 left-0 w-full z-20 bg-backgroundColor">
        <Link to={`${AppRoutes.PROFILE}/${userId}`} className="text-primary text-lg">
          <div className="flex justify-center py-2 px-4">
            <Button containerClassName="w-full" size="medium">
              {t('Profile')}
            </Button>
          </div>
        </Link>
      </div>
    </div>
  );
});
